"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.useMutate = useMutate;
var _reactQuery = require("react-query");
var _DmsFetch = require("../components/DmsFetch/DmsFetch");
var _sweetalert = _interopRequireDefault(require("sweetalert2"));
var _reactIntlUniversal = _interopRequireDefault(require("react-intl-universal"));
function _interopRequireDefault(e) { return e && e.__esModule ? e : { default: e }; }
/**
 * postData - Sends a POST request to the specified endpoint.
 * 
 * @param {Object} param0 - The parameters for the request.
 * @param {Object} param0.data - The data to be sent in the request body.
 * @param {string} param0.endPoint - The endpoint to which the request will be sent.
 * @param {string} param0.token - The authentication token for the request.
 * @returns {Promise<Object>} - The response from the API.
 */
const postData = async _ref => {
  let {
    data,
    endPoint,
    token
  } = _ref;
  return await (0, _DmsFetch.fetchData)("".concat(endPoint), 'POST', token, data);
};

/**
 * putData - Sends a PUT request to update existing data at the specified endpoint.
 * 
 * @param {Object} param0 - The parameters for the request.
 * @param {Object} param0.data - The data to be updated.
 * @param {string} param0.pk - The primary key of the row being updated.
 * @param {string} param0.endPoint - The endpoint to which the request will be sent.
 * @param {string} param0.token - The authentication token for the request.
 * @returns {Promise<Object>} - The response from the API.
 */
const putData = async _ref2 => {
  let {
    data,
    dataPk,
    endPoint,
    token
  } = _ref2;
  const pk = (data === null || data === void 0 ? void 0 : data.pk) || dataPk;
  const response = await (0, _DmsFetch.fetchData)("".concat(endPoint, "/").concat(pk), 'PUT', token, data);
  return response;
};

/**
 * deleteData - Sends a DELETE request to remove data at the specified endpoint.
 * 
 * @param {Object} param0 - The parameters for the request.
 * @param {string} param0.pk - The primary key of the data to be deleted.
 * @param {string} param0.endPoint - The endpoint to which the request will be sent.
 * @param {string} param0.token - The authentication token for the request.
 * @returns {Promise<Object>} - The response from the API.
 */
const deleteData = async _ref3 => {
  let {
    pk,
    endPoint,
    token
  } = _ref3;
  return await (0, _DmsFetch.fetchData)("".concat(endPoint, "/").concat(pk), 'DELETE', token);
};
const callMethod = variables => {
  const {
    method
  } = variables;
  if (method == 'POST') {
    return postData(variables);
  } else if (method == 'PUT') {
    return putData(variables);
  } else if (method == 'DELETE') {
    return deleteData(variables);
  }
};

/**
 * useMutate - A custom hook for performing data mutations (POST, PUT, DELETE) using React Query.
 * 
 * This hook provides a simple way to handle API requests for creating, updating, and deleting data.
 * It utilizes the `fetchData` utility to send requests and manages cache invalidation upon successful requests.
 * 
 * @returns {Object} - An object containing the mutation function and the mutation state.
 * @example
 * 
 * // Example for POST request
 * const handleCreate = async (data) => {
 *   await mutate({
 *     method: 'POST', 
 *     data: data,     
 *     endPoint: '/api/endpoint', 
 *     token: 'your-auth-token' 
 *   });
 * };
 * 
 * // Example for PUT request
 * const handleUpdate = async (data, row_pk) => {
 *   await mutate({
 *     method: 'PUT', 
 *     data: data,    
 *     endPoint: '/api/endpoint', 
 *     token: token
 *   });
 * };
 * 
 * // Example for DELETE request
 * const handleDelete = async (pk) => {
 *   await mutate({
 *     method: 'DELETE', 
 *     pk: pk,           
 *     endPoint: '/api/endpoint', 
 *     token: token 
 *   });
 * };
 */
function useMutate() {
  const queryClient = (0, _reactQuery.useQueryClient)();
  return (0, _reactQuery.useMutation)({
    mutationFn: variables => callMethod(variables),
    onSuccess: data => {
      _sweetalert.default.fire({
        icon: "success",
        title: _reactIntlUniversal.default.get("Success").d("Success")
      });
    },
    onError: error => {
      console.error("Mutation Error", error);
    }
  });
}